import { useEffect, useRef } from 'react';
import { useApi } from './useApi';

const APP_URL = 'https://app.merchantkits.com';
const API_VERSION = '2026-04';

const WEBHOOKS: Array<{ topic: string; address: string }> = [
  { topic: 'ORDERS_CREATE',     address: `${APP_URL}/webhooks/orders/create` },
  { topic: 'ORDERS_PAID',       address: `${APP_URL}/webhooks/orders/paid` },
  { topic: 'APP_UNINSTALLED',   address: `${APP_URL}/webhooks/app/uninstalled` },
];

const WEBHOOK_CREATE_MUTATION = `
  mutation webhookSubscriptionCreate(
    $topic: WebhookSubscriptionTopic!
    $webhookSubscription: WebhookSubscriptionInput!
  ) {
    webhookSubscriptionCreate(topic: $topic, webhookSubscription: $webhookSubscription) {
      webhookSubscription { id }
      userErrors { field message }
    }
  }
`;

/**
 * Runs once per app session: checks whether webhooks are registered for this
 * merchant, and if not, registers them via App Bridge 4's authenticated fetch
 * (shopify:admin/... URL), then marks them registered on our backend.
 *
 * App Bridge 4 automatically injects the correct Admin API credentials when
 * fetch is called with the shopify:admin/... scheme, so no token management
 * is needed here.
 */
export function useWebhookRegistration() {
  const { request } = useApi();
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    (async () => {
      try {
        // Check if already registered (short-circuit on every subsequent load).
        const status = await request<{ registered: boolean }>('/webhooks/status');
        if (status.registered) return;

        console.log('[SignShield] Registering webhooks via App Bridge 4…');

        // Register each webhook directly via Shopify's Admin GraphQL API.
        // App Bridge 4 intercepts the shopify:admin/... URL and adds auth.
        const results = await Promise.all(
          WEBHOOKS.map(async ({ topic, address }) => {
            const res = await fetch(
              `shopify:admin/api/${API_VERSION}/graphql.json`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  query: WEBHOOK_CREATE_MUTATION,
                  variables: {
                    topic,
                    webhookSubscription: {
                      callbackUrl: address,
                      format: 'JSON',
                    },
                  },
                }),
              },
            );
            const json = await res.json();
            const errors = json?.data?.webhookSubscriptionCreate?.userErrors ?? [];
            if (errors.length) {
              // "already exists" errors are fine — idempotent.
              const real = errors.filter(
                (e: { message: string }) => !e.message.toLowerCase().includes('already'),
              );
              if (real.length) throw new Error(`${topic}: ${real[0].message}`);
            }
            return topic;
          }),
        );

        console.log('[SignShield] Webhooks registered:', results);

        // Tell the backend so it sets webhooksRegistered = true.
        await request('/webhooks/mark-registered', { method: 'POST' });
      } catch (err) {
        // Non-fatal — webhooks will be retried on next load.
        console.warn('[SignShield] Webhook registration failed:', err);
      }
    })();
  }, [request]);
}
